import { generateUuid } from './generateUuid'
import { splitStreamText } from './stringUtil'
import { useCharacterStore } from '@/store/characterStore'
import type { ChatHistoryPayload } from '@/services/chat'
import type { Character, Message, Role } from '@/types'

/**
 * Convert the Messages[] to XML
 * In home page, when load chat from db
 * the chatHistory has to be constructed from the Messages[]
 * @param message Message[]
 * @returns ChatHistoryPayload[]
 */
export function mapMessageToXML(message: Message[]): ChatHistoryPayload[] {
  const chatHistory: ChatHistoryPayload[] = []
  let assistantMessageBuffer = ''

  message.forEach((m) => {
    if (m.role === 'user') {
      // handle the assistant message buffer
      if (assistantMessageBuffer.length) {
        chatHistory.push({
          content: assistantMessageBuffer,
          role: 'assistant',
        })
        assistantMessageBuffer = ''
      }

      // handle the user message
      const xml = createXml({ name: m.name, message: m.message })
      chatHistory.push({
        content: xml,
        role: 'user',
      })
    }
    else {
      const action = (m.action?.length || 0) > 0 ? `<action expression="${m.expression}">${m.action}</action>` : ''

      const xml = m.role === 'char'
        ? `<character name="${m.name}">${action}<say audioUrl="${m.audioUrl}">${m.message}</say></character>`
        : `<Narrator>${m.message}</Narrator>`
      assistantMessageBuffer += xml
    }
  })

  if (assistantMessageBuffer.length) {
    chatHistory.push({
      content: assistantMessageBuffer,
      role: 'assistant',
    })
  }

  return chatHistory
}

interface MsgParseModel {
  type: string
  content: string
  raw: string
}

/**
 * Convert the XML to Messages[]
 * used in universe preview and home page
 * @param input string XML format
 * @param chars Character[] to match the character name in the XML
 */

export function textToMessagesNew(
  input: string,
  chars: Character[],
  messageIdPrefix?: string,
): Message[] {
  // Debug log
  console.debug(`[textToMessageNew]: input length: ${input?.length || 0}, chars length: ${chars?.length || 0}`)

  // Check if chars is undefined or empty
  if (!chars || chars.length === 0) {
    console.debug('[textToMessageNew]: chars is undefined or empty, returning empty array')
    return []
  }

  const charMap = new Map(chars.map(char => [char.charName, char]))
  const segments = splitStreamText(input)

  const messages: Message[] = []
  let currentCharacter: Character | null | undefined = null

  for (const segment of segments) {
    if (segment.includes('<character name=') || segment.includes('<mainCharacter name=')) {
      const role = segment.includes('<mainCharacter name=') ? 'user' : 'char'
      const characterMatch = segment.match(/<(character|mainCharacter) name=["'](.*?)["']>/)
      // Here there could a case that the character is not exist in the universe
      // So we use the name from the XML directly and leave charOrUserId as 0, avatarUrl as ''
      if (characterMatch) {
        const name = characterMatch[2]
        if (charMap.has(name)) {
          currentCharacter = charMap.get(name)
        }
        else {
          // search character
          const characterStore = useCharacterStore()

          if (characterStore.characterExist(name)) {
            currentCharacter = characterStore.getCharacterByName(name)
          }
          else {
            const temp = ({
              charId: 0,
              charName: characterMatch ? characterMatch[2] : '',
              charDescription: '',
              createTimeUtc: 0,
              followerCount: 0,
              role: role as Role,
              complete: true,
            } satisfies Character)
            characterStore.addExtraCharacter(temp)
          }
        }
      }
      else {
        currentCharacter = null
      }

      // Process actions and say messages
      const items: MsgParseModel[] = []
      const actionRegex = /<action(?: expression=['"][^'"]*['"])?>(.*?)(?=<\/action>|<|$)/g
      const sayRegex = /<say\s*(?:expression=['"][^'"]*['"]\s*)?(?:audio_url=['"][^'"]*['"]\s*)?>(.*?)(?=<\/say>|<|$)/g
      const videoRegex = /<video\s+(?:[^>]*?\s+)?url=["']([^"']*)["']/g

      // Find all actions
      for (const match of segment.matchAll(actionRegex)) {
        if (match[1]) {
          items.push({
            type: 'action',
            content: match[1].trim(),
            raw: match[0],
          })
        }
      }

      // Find all say messages
      for (const match of segment.matchAll(sayRegex)) {
        if (match[1] && match[1].trim().length > 2) {
          items.push({
            type: 'say',
            content: match[1].trim(),
            raw: match[0],
          })
        }
      }

      // Find all video
      for (const match of segment.matchAll(videoRegex)) {
        if (match[1]) {
          items.push({
            type: 'video',
            content: match[1].trim(),
            raw: match[0],
          })
        }
      }
      // Sort items based on their occurrence in the segment
      items.sort(
        (a, b) => segment.indexOf(a.raw) - segment.indexOf(b.raw),
      )

      let index = 0
      while (index < items.length) {
        let action = ''
        let say = ''
        let expression = ''
        let audio_url = ''
        if (items[index].type === 'action') {
          // get the expression from the current action
          expression = parseExpression(items[index].raw) || ''
          // lower case the expression
          expression = expression.toLowerCase()
          // Concatenate successive actions
          while (index < items.length && items[index].type === 'action') {
            action += items[index].content
            index++
          }

          // Check if the current or next item is a say
          if (index < items.length && items[index].type === 'say') {
            audio_url = parseAudioUrl(items[index].raw) || ''
            say = items[index].content
            index++ // Move past the say item
          }
        }
        else if (items[index].type === 'say') {
          // get the audio_url from the current say
          audio_url = parseAudioUrl(items[index].raw) || ''
          // If the item is a say, use it directly
          say = items[index].content
          index++ // Move to the next item
        }
        else if (items[index].type === 'video') {
        // check the video url is valid
          const validUrl = items[index].content.match(/^(http|https):\/\/[^ "]+$/)
          if (validUrl) {
            messages.push({
              messageId: messageIdPrefix
                ? `${messageIdPrefix}-${messages.length}-${currentCharacter?.charName.replace(' ', '-')}`
                : generateUuid(),
              charOrUserId: currentCharacter?.charId || 0,
              role: role as Role,
              action,
              expression,
              message: say,
              name: currentCharacter?.charName || 'Unknown',
              messageTimestampUtc: Date.now(),
              poseUrl: currentCharacter?.pose?.[expression] || currentCharacter?.pose?.['original'] || '',
              audioUrl: audio_url,
              videoUrl: items[index].content,
            })
          }
          index++
        }

        // Create a message if there is an action or a say content
        if (action || say) {
          messages.push({
            messageId: messageIdPrefix
              ? `${messageIdPrefix}-${messages.length}-${currentCharacter?.charName.replace(' ', '-')}`
              : generateUuid(),
            charOrUserId: currentCharacter?.charId || 0,
            role: role as Role,
            action,
            expression,
            message: say,
            name: currentCharacter?.charName || 'Unknown',
            messageTimestampUtc: Date.now(),
            poseUrl: currentCharacter?.pose?.[expression] || currentCharacter?.pose?.['original'] || '',
            audioUrl: audio_url,
          })
        }
      }
    }
    else if (segment.includes('<Narrator>')) {
      messages.push(
        parseNarration(
          segment,
          messageIdPrefix
            ? `${messageIdPrefix}-${messages.length}`
            : generateUuid(),
        ),
      )
    }
  }
  return messages.filter(msg => msg.message || msg.action || msg.videoUrl)
}

export function parseNarration(segment: string, messageId: string): Message {
  // Handling Narrator
  let narration = segment.replace(/.*<Narrator>/, '')
  // replace </Narrator with ''
  narration = narration.replace(/<\/Narrator.*/, '')
  return {
    messageId,
    charOrUserId: 0,
    role: 'narration' as Role,
    action: '',
    message: narration ? narration.replace(/<say>/g, '').replace(/<\/say>/g, '') : '',
    name: 'Narrator',
    messageTimestampUtc: Date.now(),
  } satisfies Message
}

// New function to create xml
export function createXml(m: { name: string; message: string }): string {
  const isNarrator = m.name.length <= 0

  if (isNarrator) {
    // 如果是Narrator直接将message放进tag里面
    return `<Narrator>${m.message}</Narrator>`
  }
  // Regular expression to match action and dialogue parts
  // This regex now accounts for possible absence of action or dialogue
  const regex = /\*([^*]*)\*\s*([^*]*)|([^*]+)/g
  let match
  let xml = `<character name="${m.name}">`

  // Executing regex and checking in the loop
  match = regex.exec(m.message)
  while (match !== null) {
    const action = match[1]?.trim()
    const dialogue = match[2]?.trim() || match[3]?.trim()

    if (action) {
      xml += `<action expression="neutral">${action}</action>`
    }
    if (dialogue) {
      xml += `<say>${dialogue}</say>`
    }

    // Proceed to the next match
    match = regex.exec(m.message)
  }

  xml += isNarrator ? '</Narrator>' : '</character>'
  return xml
}

function parseExpression(input: string): string | null {
  const regex = /<action expression=['"]([^'"]+)['"]>/
  const matches = input.match(regex)

  if (matches && matches.length >= 2) {
    return matches[1]
  }

  return null
}

function parseAudioUrl(input: string): string | null {
  const regex = /<say audio_url=['"]([^'"]+)['"]>/
  const matches = input.match(regex)
  if (matches && matches.length >= 2) {
    return matches[1]
  }
  return null
}
